import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type QuantumOverride = { n: number; l: number; m: number };

export type SelectedElementState = {
  element: string;
  name: string;
  type: string;
  description: string;
  electronConfig?: string;
  quantumOverride?: QuantumOverride;
  /** Subshell label ("3d") to open on instead of the outermost one. */
  initialSubshell?: string;
};

export type RenderUiState = {
  animation: boolean;
  disableButton: boolean;
  preRender: boolean;
  serverError: boolean;
  statusText: string;
};

export type AtomData = Record<string, unknown> | null;

const defaultSelection: SelectedElementState = {
  element: "H2",
  name: "Hydrogen Gas",
  type: "Molecule",
  description:
    "Hydrogen is the first element in the periodic table. The atomic number is 1 and its mass is 1.01 g/mol. Its gas form consists of two Hydrogen atoms, forming a Sigma bond.",
};

const defaultRenderUi: RenderUiState = {
  animation: false,
  disableButton: false,
  preRender: true,
  serverError: false,
  statusText: "Working hard...",
};

export type ViewerState = {
  atomData: AtomData;
  render: RenderUiState;
  selection: SelectedElementState;
};

const initialState: ViewerState = {
  atomData: null,
  render: defaultRenderUi,
  selection: defaultSelection,
};

export const viewerSlice = createSlice({
  name: "viewer",
  initialState,
  reducers: {
    setAtomData(state, action: PayloadAction<AtomData>) {
      state.atomData = action.payload;
    },
    mergeAtomData(state, action: PayloadAction<Record<string, unknown>>) {
      const base =
        state.atomData && typeof state.atomData === "object"
          ? { ...state.atomData }
          : {};
      state.atomData = { ...base, ...action.payload };
    },
    setRender(state, action: PayloadAction<RenderUiState>) {
      state.render = action.payload;
    },
    setSelection(state, action: PayloadAction<SelectedElementState>) {
      state.selection = action.payload;
    },
  },
});

export const {
  setAtomData,
  mergeAtomData,
  setRender,
  setSelection,
} = viewerSlice.actions;

export default viewerSlice.reducer;
